import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import { createRequestReducer } from '@fullstory/redux-util/middleware/request/reducers';
import { WatchedElementActions, WatchedElementRequestActions } from './actions';
import {
  WatchedElementsReducerState as $WatchedElementsReducerState,
  WatchedElementResponse,
  WatchedElements,
  WatchedElement,
} from './types';
import { ElementWatch } from '@fullstory/proto/fs/services/orgs';
import { WebRecordingSettingsTypes } from '../settings/webRecording/types';
import { WebRecordingSettingsRequestActions } from '../settings';
import { NamedElementsRequestTypes } from '../namedElements/types';
import { NamedElementsActions } from '../namedElements';

const toWatchedElement = (w: ElementWatch): WatchedElement => ({
  namedElementId: w.namedElementId,
  label: w.label,
  notes: w.notes,
  selectors: [w.selector],
  lastUpdated: w.lastUpdated,
});

const watches = (
  state: $WatchedElementsReducerState = [],
  action:
    | WatchedElementActions
    | WatchedElementRequestActions
    | WebRecordingSettingsRequestActions
    | NamedElementsActions,
): $WatchedElementsReducerState => {
  switch (action.type) {
    case WebRecordingSettingsTypes.FetchSuccess: {
      return action.data.watches.map(toWatchedElement);
    }
    /**
     * The action format here is WatchedElementResponse which contains
     * the new updated list of all watches anytime one is created
     * updated or deleted.
     */
    case WatchedElements.CreateWatchedElementSuccess:
    case WatchedElements.UpdateWatchedElementSuccess:
    case WatchedElements.UpdateWatchedElementsSuccess:
    case WatchedElements.DeleteWatchedElementSuccess: {
      return action.data.watches.map(toWatchedElement);
    }
    case NamedElementsRequestTypes.ArchiveNamedElementSuccess: {
      // When archiving a named element, remove from watches
      return state.filter(
        we => we.namedElementId !== action.data.namedElement?.namedElement?.namedElement?.id,
      );
    }
  }
  return state;
};

const watchesInitialized = (state: boolean = false, action: WatchedElementActions): boolean => {
  switch (action.type) {
    case WatchedElements.SetWatchedElements: {
      return true;
    }
  }
  return state;
};

const createWatch = createRequestReducer<WatchedElementResponse>()(
  WatchedElements.CreateWatchedElement,
  WatchedElements.CreateWatchedElementSuccess,
  WatchedElements.CreateWatchedElementFailure,
);

const updateWatch = createRequestReducer<WatchedElementResponse>()(
  WatchedElements.UpdateWatchedElement,
  WatchedElements.UpdateWatchedElementSuccess,
  WatchedElements.UpdateWatchedElementFailure,
);

const updateWatches = createRequestReducer<WatchedElementResponse>()(
  WatchedElements.UpdateWatchedElement,
  WatchedElements.UpdateWatchedElementSuccess,
  WatchedElements.UpdateWatchedElementFailure,
);

const deleteWatch = createRequestReducer<WatchedElementResponse>()(
  WatchedElements.DeleteWatchedElement,
  WatchedElements.DeleteWatchedElementSuccess,
  WatchedElements.DeleteWatchedElementFailure,
);

type WatchedElementsReducerState = {
  watches: ReturnType<typeof watches>;
  watchesInitialized: ReturnType<typeof watchesInitialized>;
  createWatch: ReturnType<typeof createWatch>;
  updateWatch: ReturnType<typeof updateWatch>;
  updateWatches: ReturnType<typeof updateWatches>;
  deleteWatch: ReturnType<typeof deleteWatch>;
};

export const watchedElementsReducer = combineReducers<WatchedElementsReducerState>({
  watches,
  watchesInitialized,
  createWatch,
  updateWatch,
  updateWatches,
  deleteWatch,
});
